/* ==========================================================================
   Delivery Estimate — v3 (auto-detects MESTERINO, no Liquid edits needed)
   --------------------------------------------------------------------------
   • Auto-detects MESTERINO tag by fetching /products/{handle}.js on product pages
   • Year-agnostic Christmas/New-Year warning logic
   • Europe/Bucharest timezone anchor
   • Set-based holiday lookup (fixed yearly + Easter-based year-specific)
   • IIFE-wrapped (no globals leaked)
   • Stock-aware via theme's Add-to-Cart button inside [data-product-form]

   Windows
   -------
   • Normal product:    ship+1 to ship+4 business days
   • MESTERINO product: ship+5 to ship+7 business days
   • Cutoff: 15:00 Europe/Bucharest. Past cutoff or non-biz day → ships next biz day.

   Tag detection (priority order)
   ------------------------------
   1. Element has data-mesterino="1"                    (explicit override)
   2. Ancestor has data-mesterino="1"                   (explicit override)
   3. Product JSON contains 'MESTERINO' tag             (auto, case-insensitive)

   Debug: set window.__deliveryDebug = true before this script loads to see
   console logs about tag detection.
   ========================================================================== */
(() => {
  'use strict';

  // ── Config ──────────────────────────────────────────────────────────────
  const CUTOFF        = 15;
  const DELIVERY_MIN  = 1;
  const DELIVERY_MAX  = 4;
  const MESTERINO_MIN = 5;
  const MESTERINO_MAX = 7;
  const MESTERINO_TAG = 'mesterino';          // compared lowercase
  const TZ            = 'Europe/Bucharest';
  const VARIANT_DELAY = 80;
  const DEBUG         = !!window.__deliveryDebug;

  const log = (...a) => DEBUG && console.log('[delivery]', ...a);

  // ── Holidays ────────────────────────────────────────────────────────────
  const HOL_FIXED = new Set([
    '01-01','01-02','01-06','01-07',
    '05-01','06-01','08-15','11-30','12-01','12-25','12-26'
  ]);
  const HOL_DATED = new Set([
    '2026-04-10','2026-04-12','2026-04-13','2026-05-31','2026-06-01',
    '2027-04-30','2027-05-02','2027-05-03','2027-06-20','2027-06-21',
    '2028-04-14','2028-04-16','2028-04-17','2028-06-04','2028-06-05'
  ]);

  const MONTHS = ['Ian','Feb','Martie','Apr','Mai','Iunie','Iulie','Aug','Sept','Oct','Noi','Dec'];

  // ── Helpers ─────────────────────────────────────────────────────────────
  const pad = n => String(n).padStart(2, '0');
  const md  = d => `${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
  const ymd = d => `${d.getFullYear()}-${md(d)}`;

  const isHoliday = d => HOL_FIXED.has(md(d)) || HOL_DATED.has(ymd(d));
  const isBiz     = d => { const w = d.getDay(); return w !== 0 && w !== 6 && !isHoliday(d); };
  const addBiz    = (date, n) => {
    const r = new Date(date);
    let c = 0;
    while (c < n) { r.setDate(r.getDate()+1); if (isBiz(r)) c++; }
    return r;
  };
  const fmtDate = d => `${d.getDate()} ${MONTHS[d.getMonth()]}`;
  const nowTZ   = () => new Date(new Date().toLocaleString('en-US', { timeZone: TZ }));

  const timeUntilCutoff = () => {
    const n = nowTZ();
    const c = new Date(n);
    c.setHours(CUTOFF, 0, 0, 0);
    if (n.getHours() >= CUTOFF) c.setDate(c.getDate()+1);
    const diff = c - n;
    return { h: Math.floor(diff / 3600000), m: Math.floor(diff % 3600000 / 60000) };
  };

  // ── Product tag loader (fetch /products/{handle}.js) ───────────────────
  // Detects current product handle from URL and caches tags for the session.
  let productTags = null;
  let tagPromise  = null;

  const loadTags = () => {
    if (tagPromise) return tagPromise;
    const m = location.pathname.match(/^\/products\/([^\/\?#]+)/);
    if (!m) {
      log('not a product page, skipping tag fetch');
      productTags = [];
      return tagPromise = Promise.resolve([]);
    }
    const handle = m[1];
    log('fetching tags for handle:', handle);
    tagPromise = fetch(`/products/${handle}.js`, { credentials: 'same-origin' })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        productTags = (d && Array.isArray(d.tags)) ? d.tags : [];
        log('tags loaded:', productTags);
        return productTags;
      })
      .catch(err => {
        log('tag fetch failed:', err);
        productTags = [];
        return productTags;
      });
    return tagPromise;
  };

  // ── MESTERINO detection ────────────────────────────────────────────────
  const isMesterino = (el) => {
    if (el.dataset.mesterino === '1') return true;
    if (el.closest('[data-mesterino="1"]')) return true;
    if (!productTags) return false;
    return productTags.some(t => String(t).toLowerCase() === MESTERINO_TAG);
  };

  // ── Holiday warning (year-agnostic) ────────────────────────────────────
  const holidayWarning = (deliveryEnd) => {
    const now = nowTZ();
    now.setHours(0, 0, 0, 0);
    const y = now.getFullYear(), m = now.getMonth(), d = now.getDate();

    let xy;
    if (m === 11 || (m === 10 && d >= 24)) xy = y;
    else if (m === 0 && d <= 10)           xy = y - 1;
    else return '';

    const xmas = new Date(xy, 11, 25);     xmas.setHours(0,0,0,0);
    const ny   = new Date(xy + 1, 0, 1);   ny.setHours(0,0,0,0);
    const end  = new Date(deliveryEnd);    end.setHours(0,0,0,0);

    const pastXmas = y > xy || (m === 11 && d >= 25);
    const isDec24  = y === xy && m === 11 && d === 24;

    let text = '';
    if (pastXmas) {
      if (end > ny) text = `Posibil să primești comanda după 12.01.${xy + 1}`;
    } else if (isDec24) {
      if (end > xmas) text = 'Vei primi comanda după Crăciun';
    } else {
      if (end > xmas) text = 'Posibil să primești comanda după Crăciun';
    }

    return text ? `<p class="holiday-warning">⚠️ ${text}</p>` : '';
  };

  // ── Message builder ─────────────────────────────────────────────────────
  const buildMessage = (el) => {
    const mes  = isMesterino(el);
    const dmin = mes ? MESTERINO_MIN : DELIVERY_MIN;
    const dmax = mes ? MESTERINO_MAX : DELIVERY_MAX;
    log('render →', mes ? 'MESTERINO 5-7d' : 'normal 1-4d');

    const now = nowTZ();
    let ship = new Date(now);
    if (now.getHours() >= CUTOFF || !isBiz(now)) ship = addBiz(ship, 1);

    const s = addBiz(ship, dmin);
    const e = addBiz(ship, dmax);
    const t = timeUntilCutoff();

    return `<p>✓ <span class="in-stock">În Stoc</span> Comandă în următoarele <span class="bold">${t.h}h ${t.m}m</span> și vei primi comanda între <span class="bold">${fmtDate(s)} - ${fmtDate(e)}</span></p>${holidayWarning(e)}`;
  };

  // ── Stock detection ────────────────────────────────────────────────────
  const inStock = (el) => {
    const scope = el.closest('[data-product-form]') || el.closest('form[action*="/cart/add"]') || document;
    const btn   = scope.querySelector('button[name="add"]');
    if (!btn) return true;
    if (btn.disabled || btn.classList.contains('disabled')) return false;
    const t = (btn.textContent || '').toLowerCase();
    return !t.includes('stoc epuizat') && !t.includes('sold out');
  };

  // ── Render ──────────────────────────────────────────────────────────────
  const renderOne = (el) => {
    if (!inStock(el)) { el.innerHTML = ''; el.style.display = 'none'; return; }
    el.style.display = '';
    el.innerHTML = buildMessage(el);
  };

  const renderAll = () => {
    document.querySelectorAll('.delivery-estimate').forEach(renderOne);
  };

  // ── Bootstrap ───────────────────────────────────────────────────────────
  // Load tags first, then render. Avoids a "1-4 days" flash on MESTERINO products.
  const init = () => {
    loadTags().then(renderAll);

    document.addEventListener('change', (e) => {
      const t = e.target;
      if (!t) return;
      if (t.name === 'id' || (t.closest && t.closest('[data-product-form]'))) {
        setTimeout(renderAll, VARIANT_DELAY);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();